'use client';

// file imports
import { IQuestion, QuestionOption, isQuestionOptionObject, isStringOption } from '@/types';

interface QuestionEditorProps {
  selectedQuestion: IQuestion | null;
  editingQuestion: IQuestion | null;
  orderedQuestions: IQuestion[];
  selectedOptions: Record<string, boolean>;
  requiredOptions: Record<string, boolean>;
  canEditQuestions: boolean;
  onDescriptionChange: (description: string) => void;
  onOptionToggle: (optionKey: string) => void;
  onRequiredToggle: (optionKey: string) => void;
}

export default function QuestionEditor({ selectedQuestion, editingQuestion, orderedQuestions, selectedOptions, requiredOptions, canEditQuestions, onDescriptionChange, onOptionToggle, onRequiredToggle }: QuestionEditorProps) {
  if (!selectedQuestion) {
    return (
      <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
        <div className="text-gray-500 mb-2">
          <svg className="w-12 h-12 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-700 mb-2">No question selected</h3>
        <p className="text-gray-500">Select a question from the sidebar to view and edit it.</p>
      </div>
    );
  }

  const questionNumber = orderedQuestions.findIndex((q) => q._id === selectedQuestion._id) + 1;

  return (
    <div className="space-y-4">
      {/* Hidden Question Warning */}
      {!selectedQuestion.isEnabled && (
        <div className="bg-gray-100 border border-gray-200 rounded-lg p-4 mb-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
              <svg className="w-4 h-4 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z" clipRule="evenodd" />
                <path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-800">
                <span className="text-red-600 font-semibold">Your audience will NOT see this question.</span>
                <span className="text-gray-600 ml-1">Toggle the</span>
                <span className="text-gray-600 ml-1">👁</span>
                <span className="text-gray-600 ml-1">to make this question visible.</span>
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-start gap-4">
          <div className="hidden sm:flex w-8 h-8 bg-gray-800 text-white rounded-lg items-center justify-center font-semibold text-sm">{questionNumber}</div>
          <div className="flex-1">
            <div className="mb-4">
              <div className="flex bg-gray-100 rounded-lg overflow-hidden shadow-sm">
                <div className="bg-gray-200 px-4 py-3 flex items-center">
                  <span className="text-sm text-gray-700 font-medium">Question</span>
                </div>
                <input type="text" value={editingQuestion?.description || selectedQuestion.label || ''} onChange={(e) => onDescriptionChange(e.target.value)} placeholder="Let's get your contact info" disabled={!canEditQuestions} title={!canEditQuestions ? 'Upgrade your plan to edit questions' : ''} className={`flex-1 px-4 py-3 bg-white text-gray-700 border-none outline-none placeholder-gray-400 ${!canEditQuestions ? 'text-gray-500 cursor-not-allowed' : ''}`} />
              </div>
            </div>

            {selectedQuestion.options && selectedQuestion.options.length > 0 && (
              <div className="space-y-2">
                {selectedQuestion.type === 'contact' ? (
                  // Contact info questions - show only "Make required" checkboxes
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="text-sm font-medium text-gray-700 mb-2">Available options:</div>
                    <div className="space-y-2">
                      {selectedQuestion.options.map((option: QuestionOption, optIndex: number) => {
                        const optionKey = isStringOption(option) ? option : option.label;

                        // Check if the under-18 option is selected by finding it in the options
                        const under18Option = selectedQuestion.options?.find((opt: QuestionOption) => isQuestionOptionObject(opt) && opt.isUnder18Option);
                        const under18OptionKey = under18Option ? (isStringOption(under18Option) ? under18Option : under18Option.label) : '';
                        const isUnder18Selected = selectedOptions[under18OptionKey] || false;

                        const isEmailOrName = optionKey === 'Ask for Attendee Email' || optionKey === 'Ask for Attendee Name';
                        const isUnder18Option = isQuestionOptionObject(option) && option.isUnder18Option;

                        return (
                          <div key={optIndex} className="space-y-2">
                            <div className="flex items-center gap-3">
                              {isUnder18Option ? (
                                // Under 18 option - show checkbox
                                <>
                                  <input type="checkbox" className={`w-4 h-4 text-blue-600 ${!canEditQuestions ? 'opacity-50 cursor-not-allowed' : ''}`} checked={isUnder18Selected} onChange={() => onOptionToggle(optionKey)} disabled={!canEditQuestions} />
                                  <span className={`text-sm text-gray-700 ${canEditQuestions ? 'cursor-pointer' : 'cursor-not-allowed'}`} onClick={canEditQuestions ? () => onOptionToggle(optionKey) : undefined}>
                                    {optionKey}
                                  </span>
                                </>
                              ) : (
                                // Regular options - show field name and required checkbox
                                <>
                                  <span className={`text-sm text-gray-700 ${isUnder18Selected && isEmailOrName ? 'text-gray-400' : ''}`}>{optionKey}</span>
                                  <div className="flex items-center gap-2">
                                    <input type="checkbox" className={`w-3 h-3 text-blue-600 ${!canEditQuestions || (isUnder18Selected && isEmailOrName) ? 'opacity-50 cursor-not-allowed' : ''}`} checked={requiredOptions[optionKey] || false} onChange={() => onRequiredToggle(optionKey)} disabled={!canEditQuestions || (isUnder18Selected && isEmailOrName)} />
                                    <span className={`text-xs text-gray-500 ${canEditQuestions && !(isUnder18Selected && isEmailOrName) ? 'cursor-pointer' : 'cursor-not-allowed'}`} onClick={canEditQuestions && !(isUnder18Selected && isEmailOrName) ? () => onRequiredToggle(optionKey) : undefined}>
                                      Make required
                                    </span>
                                  </div>
                                </>
                              )}
                            </div>
                            {isUnder18Option && isUnder18Selected && (
                              <div className="ml-7">
                                <p className="text-xs text-red-600 bg-red-50 p-2 rounded border border-red-200">When checked, anyone who identifies as under 18 will not have any personal information such as their name or email collected.</p>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  // Other question types - show options as simple list
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="text-sm font-medium text-gray-700 mb-2">Available options:</div>
                    <ul className="space-y-1">
                      {selectedQuestion.options.map((option: QuestionOption, optIndex: number) => {
                        const optionKey = isStringOption(option) ? option : option.label;
                        return (
                          <li key={optIndex} className="text-sm text-gray-600 flex items-center gap-2">
                            <span className="w-1.5 h-1.5 bg-gray-400 rounded-full"></span>
                            {optionKey}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
